import type { KeyboardEvent, MouseEvent, SubmitEvent} from 'react';
import { lazy, Suspense, useCallback, useState } from 'react';
import { useDebounceCallback } from 'usehooks-ts';
import gtag from '../analytics/gtag';
import * as api from '../api';
import { autocompleteURL, barcodeSearchURL } from '../config/SearchAPIConfig';
import { searchURL } from '../config/SiteConfig';
import Icon from '../generic/Icon';
import type { FormattedNumber, Product } from '../model/types';
import VolumeAndPrice from '../product/VolumeAndPrice';
import eventTargetValue from '../utils/eventTargetValue';
import { includeInCsp, includeScriptHashInCsp } from '../utils/HeaderContext';
import preventDefault from '../utils/preventDefault';
import useBooleanState from '../utils/useBooleanState';
import * as style from './SearchBox.module.css';

const BarcodeScanner = lazy(() => import('./BarcodeScanner'));
const setSarchValueScript = `document.currentScript.previousSibling.value = new URLSearchParams(location.search).get('q')?.split(':')[0] ?? '';`;

export default function SearchBox() {
    const [query, setQuery] = useState(getQuery);
    const [suggestions, setSuggestions] = useState<SuggestionGroup[]>([]);

    const debouncedAutoComplete = useDebounceCallback(async (query: string) => {
        const suggestions = await autoComplete(query.toLowerCase());

        setSuggestions(suggestions);
    }, 300);

    const onChange = useCallback((value: string) => {
        setQuery(value);
        debouncedAutoComplete(value);
    }, []);

    includeScriptHashInCsp(setSarchValueScript);
    includeInCsp('script-src', `'wasm-unsafe-eval'`);

    return (
        <form id="header-search-form" action={searchURL()} onSubmit={onSubmit} className={style.search} autoComplete="off" onKeyDown={onKeyDown}>
            <button type="submit" className={style.searchButton} tabIndex={-1} aria-label="Start søk i nettsiden">
                <Icon className="icon-global-search" />
            </button>
            <input type="search" name="q" value={query} onChange={eventTargetValue(onChange)} placeholder="Søk" autoComplete="off" />
            {/* This is a hacky solution to filling the searchbox before react loads, since that takes a very long time */}
            <script
                dangerouslySetInnerHTML={{
                    __html: setSarchValueScript,
                }}
            ></script>
            <input type="hidden" name="searchType" value="product" />
            <BarcodeButtonAndScanner />
            {!!suggestions.length && (
                <ul className={style.autocomplete}>
                    {suggestions.flatMap((s) => [
                        <li key={s.value} className={style.suggestionType}>
                            {s.name}
                        </li>,
                        ...s.elements.map(({ highlightedText, searchQuery, searchUrl, product }) => (
                            <li key={searchQuery}>
                                {product ? (
                                    <a href={product.url} tabIndex={-1} className={style.product} onClick={trackAutosuggest} data-category={s.value}>
                                        <img src={product.images?.at(0)?.url} />
                                        <span className={style.productCategory}>
                                            {product.main_sub_category?.name ?? product.main_category?.name}
                                        </span>
                                        <div className={style.productName} data-link-text>
                                            {highlight(highlightedText)}
                                        </div>
                                        <VolumeAndPrice volume={product.volume} price={product.price} />
                                    </a>
                                ) : (
                                    <a
                                        href={searchUrl ?? searchURL(searchQuery)}
                                        tabIndex={-1}
                                        className={style.suggestion}
                                        onClick={trackAutosuggest}
                                        data-category={s.value}
                                    >
                                        <div className={style.text} data-link-text>
                                            {highlight(highlightedText)}
                                        </div>
                                    </a>
                                )}
                            </li>
                        )),
                    ])}
                </ul>
            )}
        </form>
    );
}

function BarcodeButtonAndScanner() {
    const [showBarcodeScanner, setShowBarcodeScanner] = useBooleanState();

    return (
        <Suspense fallback={<BarcodeButton onClick={setShowBarcodeScanner.toggle} loading />}>
            <BarcodeButton onClick={setShowBarcodeScanner.toggle} />
            {showBarcodeScanner && <BarcodeScanner onDetected={barcodeSearch} onClose={setShowBarcodeScanner.toFalse} />}
        </Suspense>
    );
}

function BarcodeButton({ onClick, loading }: { onClick: () => void; loading?: boolean }) {
    return (
        <button
            type="button"
            className={style.barcodeButton}
            onClick={preventDefault(onClick, trackBarcodeButtonClicked)}
            aria-label="Søk med strekkode"
            aria-busy={loading}
        >
            <Icon className={style.iconBarcode} />
        </button>
    );
}

function trackBarcodeButtonClicked(event: MouseEvent<HTMLButtonElement>) {
    gtag('event', 'header_link_click', {
        link_text: event.currentTarget.getAttribute('aria-label'),
        link_url: undefined,
    });
}

function onSubmit(event: SubmitEvent<HTMLFormElement>) {
    trackSearch(event);
    setSearchType(event);
}

function setSearchType(event: SubmitEvent<HTMLFormElement>) {
    event.currentTarget.searchType.value = new URLSearchParams(location.search).get('searchType') ?? 'product';
}

function trackSearch(event: SubmitEvent<HTMLFormElement>) {
    gtag('event', 'search', {
        search_term: event.currentTarget.querySelector('input')?.value,
        search_suggestion: false,
        link_text: undefined,
        search_category: undefined,
    });
}

function trackAutosuggest(event: MouseEvent<HTMLAnchorElement>) {
    gtag('event', 'search', {
        search_term: event.currentTarget.closest('form')?.querySelector('input')?.value,
        search_suggestion: true,
        link_text: event.currentTarget.querySelector('[data-link-text]')?.textContent,
        search_category: event.currentTarget.getAttribute('data-category'),
    });
}

function onKeyDown(event: KeyboardEvent<HTMLElement>) {
    if (event.key === 'ArrowUp' || event.key === 'ArrowDown') {
        const activeElement = document.activeElement;
        const direction = event.key === 'ArrowUp' ? -1 : +1;

        if (activeElement instanceof HTMLElement) {
            const all = Array.from<HTMLElement>(event.currentTarget?.querySelectorAll('input,a'));
            const current = all.indexOf(activeElement);
            all[current + direction]?.focus();
            event.preventDefault();
        }
    }
}

const startTag = '<mark>';
const endTag = '</mark>';
function highlight(text: string) {
    const chunks = [];
    let index = 0;
    while (index < text.length) {
        const start = text.indexOf(startTag, index);
        const end = text.indexOf(endTag, start);
        if (start >= 0 && end >= 0) {
            if (index !== start) {
                chunks.push(<span key={index}>{text.slice(index, start)}</span>);
            }
            chunks.push(<mark key={start}>{text.slice(start + startTag.length, end)}</mark>);
            index = end + endTag.length;
        } else {
            chunks.push(<span key={index}>{text.slice(index)}</span>);
            index = text.length;
        }
    }

    return chunks;
}

function getQuery() {
    if (typeof location === 'undefined') return '';
    return new URLSearchParams(location.search).get('q')?.split(':')[0] ?? '';
}

interface SuggestedProduct extends Product {
    price: FormattedNumber;
}

interface SuggestionElement {
    searchQuery?: string;
    searchUrl?: string;
    text: string;
    highlightedText: string;
    product?: SuggestedProduct;
}

interface SuggestionGroup {
    value: string;
    name: string;
    elements: SuggestionElement[];
}

interface AutocompleteResponse {
    suggestionGroups: SuggestionGroup[];
}

async function autoComplete(term: string) {
    if (term.length < 3) return [];
    const { suggestionGroups } = await api.getAnonymously<AutocompleteResponse>(autocompleteURL(), { term, onlyProducts: 'false' });
    return suggestionGroups;
}

async function barcodeSearch(barcode: string) {
    const { url, name } = await api.getAnonymously<{ url: string; name: string }>(barcodeSearchURL(barcode), {
        fields: 'FULL',
    });

    gtag('event', 'search', {
        search_term: barcode,
        search_suggestion: false,
        link_text: undefined,
        search_category: 'barcode-scanner',
    });

    location.assign(url);

    return { url, name };
}
